// Copyright (c) 2026 SoundDrift. All rights reserved.

import React, { useState, useRef, useEffect } from 'react';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

/**
 * AudioAudition component for previewing/auditioning sound clips at reduced gain.
 * Per REQUIREMENTS §2:
 * - Reduced gain auditioning on player devices
 * - Renders per-item attribution for Openverse/CC clips
 *
 * Visual design matches design/canvas/Main.dc.html audition pad:
 * - 44×44 pad, borderRadius 10px
 * - Idle: play glyph on --sd-panel-2, icon #8A939C
 * - Playing: pause glyph on clip accent, icon --sd-ground
 */
export default function AudioAudition({
  clip,
  selected = false,
  selectLabel = null,
  onSelect = null,
  gain = 0.35,
  showAttribution = true,
  disabled = false,
  className = '',
  style = {},
  color = null,
  compact = false,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const accentBg = color || clip?.color || 'var(--sd-teal, #3ACFCF)';

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleTogglePlay = (e) => {
    e.stopPropagation();
    if (disabled) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const rawUrl = clip.formats?.opus || clip.formats?.mp3 || clip.audioUrl || clip.url;
    if (!rawUrl) return;
    const audioUrl = rawUrl.startsWith('/') || rawUrl.startsWith('http') ? rawUrl : `/${rawUrl}`;

    if (!audioRef.current) {
      const audio = new Audio(audioUrl);
      audio.volume = Math.max(0, Math.min(1, gain)); // Reduced gain control

      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        setProgress(0);
      };
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      audio.onerror = () => {
        setIsPlaying(false);
        setProgress(0);
      };

      audioRef.current = audio;
    } else {
      audioRef.current.volume = Math.max(0, Math.min(1, gain));
    }

    audioRef.current.play().catch(() => {
      setIsPlaying(false);
    });
  };

  const attrText = clip.attributionString ||
    (clip.attribution ? (clip.attribution.attributionText || clip.attribution.creator) : null);

  // Compact mode: just the 44×44 audition pad button
  if (compact || !onSelect) {
    return (
      <button
        type="button"
        data-testid={`play-btn-${clip.id}`}
        data-state={isPlaying ? 'playing' : 'idle'}
        onClick={handleTogglePlay}
        disabled={disabled}
        aria-label={isPlaying ? `Stop ${clip.label || 'Clip'}` : `Audition ${clip.label || 'Clip'}`}
        style={{
          width: '44px',
          height: '44px',
          minWidth: '44px',
          minHeight: '44px',
          borderRadius: '10px',
          background: isPlaying ? accentBg : '#171C22',
          border: 'none',
          color: isPlaying ? '#08090B' : '#8A939C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          transition: 'background 0.15s ease, color 0.15s ease',
          padding: 0,
          ...style,
        }}
      >
        {isPlaying ? (
          <svg width="15" height="15" viewBox="0 0 14 14" fill={isPlaying ? '#08090B' : '#8A939C'} aria-hidden="true" data-testid="pause-icon">
            <path d="M3.5 2.6h2.6v8.8H3.5zM7.9 2.6h2.6v8.8H7.9z" />
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 14 14" fill="#8A939C" aria-hidden="true" data-testid="play-icon">
            <path d="M4 2.4l7.2 4.6L4 11.6z" />
          </svg>
        )}
      </button>
    );
  }

  // Full card mode: audition pad + clip info + pick button
  return (
    <div
      className={`audio-audition-card ${selected ? 'selected' : ''} ${className}`}
      data-testid={`audition-clip-${clip.id}`}
      style={{
        background: selected ? 'rgba(255, 255, 255, 0.028)' : '#0E1116',
        border: `1px solid ${selected ? (accentBg) : '#1C2229'}`,
        borderRadius: '12px',
        padding: '12px 14px',
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        transition: 'border-color 0.15s ease',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          <button
            type="button"
            data-testid={`play-btn-${clip.id}`}
            data-state={isPlaying ? 'playing' : 'idle'}
            onClick={handleTogglePlay}
            disabled={disabled}
            style={{
              width: '44px',
              height: '44px',
              minWidth: '44px',
              minHeight: '44px',
              borderRadius: '10px',
              background: isPlaying ? accentBg : '#171C22',
              border: 'none',
              color: isPlaying ? '#08090B' : '#8A939C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              transition: 'background 0.15s ease, color 0.15s ease',
              padding: 0,
            }}
            aria-label={isPlaying ? `Stop ${clip.label || 'Clip'}` : `Audition ${clip.label || 'Clip'}`}
          >
            {isPlaying ? (
              <svg width="15" height="15" viewBox="0 0 14 14" fill="#08090B" aria-hidden="true" data-testid="pause-icon">
                <path d="M3.5 2.6h2.6v8.8H3.5zM7.9 2.6h2.6v8.8H7.9z" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 14 14" fill="#8A939C" aria-hidden="true" data-testid="play-icon">
                <path d="M4 2.4l7.2 4.6L4 11.6z" />
              </svg>
            )}
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              title={formatClipTitle(clip, 100)}
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: selected ? '#EDEFF2' : '#C6CDD4',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                letterSpacing: '-0.005em',
                minWidth: 0,
              }}
            >
              {formatClipTitle(clip)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              {clip.source && (
                <span style={{
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#6E7982',
                }}>
                  {clip.source === 'openverse' ? 'CC' : clip.source === 'generative' ? 'AI' : clip.category || 'Library'}
                </span>
              )}
              {clip.duration && (
                <span style={{
                  fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  fontSize: '10.5px',
                  color: '#6E7982',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {clip.duration.toFixed(1)}s
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onSelect(clip)}
          disabled={disabled}
          data-testid={`select-btn-${clip.id}`}
          style={{
            minHeight: '44px',
            minWidth: '44px',
            padding: '0 16px',
            borderRadius: '10px',
            background: selected ? accentBg : '#171C22',
            border: selected ? `1px solid ${accentBg}` : '1px solid #1C2229',
            color: selected ? '#08090B' : '#C6CDD4',
            fontWeight: 600,
            fontSize: '13px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
            flexShrink: 0,
          }}
        >
          {selectLabel ? selectLabel : (selected ? '✓ Picked' : 'Pick')}
        </button>
      </div>

      {isPlaying && (
        <div style={{ width: '100%', height: '4px', background: '#1C2229', borderRadius: '2px', overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: accentBg,
              transition: 'width 0.1s linear'
            }}
          />
        </div>
      )}

      {showAttribution && attrText && (
        <div
          data-testid={`attribution-${clip.id}`}
          style={{
            fontSize: '11px',
            color: '#79838D',
            fontStyle: 'italic',
            borderTop: '1px dashed #1C2229',
            paddingTop: '6px',
            marginTop: '2px',
            wordBreak: 'break-word'
          }}
        >
          {attrText}
        </div>
      )}
    </div>
  );
}
